// src/pages/ModularDCSentry.jsx
// Modular DC Sentry - AI-Powered Modular Data Center Operations
// Edge AI Platform for Containerized High-Density Data Centers
// MWC Barcelona Demo - Celerway Stratus - FLAGSHIP PRODUCT
// 
// This demo showcases Celerway's modular data center solution
// with detailed container-level and rack-level monitoring

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// MOCK MODULAR DC DATA - Celerway High-Density Containerized Data Center
// ════════════════════════════════════════════════════════════════════════════

const MOCK_SITE_GATEWAY = {
  id: 'gateway-mdc-oslo',
  name: 'Oslo Edge Campus',
  location: 'Lørenskog, Norway',
  siteType: 'Greenfield Deployment',
  status: 'Operational',
  deploymentDate: '2024-09-15',
  totalContainers: 7,
  totalRacks: 36,
  totalITLoad: 720,           // kW
  maxCapacity: 1200,          // kW
  pue: 1.48,
  pueTrend: 'up',             // Trending up (bad)
  previousPUE: 1.42,
  availability: 99.982,       // %
  stratusModel: 'Stratus 5G Edge',
  connectivity: '5G + Fiber Backhaul',
  ambientTemp: -5,            // °C (Norway winter)
  freeCoolingActive: true,
};

// Container Modules
const MOCK_CONTAINERS = [
  // ══════════════════════════════════════════════════════════════════════════
  // DATA CONTAINERS (Compute/Storage)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'DATA-MOD-01',
    type: 'Compute Container',
    category: 'compute',
    model: 'Celerway MDC-40 High Density',
    dimensions: '40ft ISO',
    status: 'operational',
    location: 'Row A - Position 1',
    commissioned: '2024-09-15',
    rackCount: 12,
    maxRackPower: 25,         // kW per rack
    currentLoad: 245,         // kW total
    maxLoad: 300,             // kW total
    metrics: {
      // Environmental
      supplyAirTemp: 18,      // °C (cold aisle)
      returnAirTemp: 35,      // °C (hot aisle)
      deltaT: 17,
      humidity: 45,           // %
      dewPoint: 6,
      pressureDiff: 0.02,     // inches WC (containment)
      // Power
      powerA: 124,            // kW (A feed)
      powerB: 121,            // kW (B feed)
      totalPower: 245,
      // Cooling
      cracUnits: 4,
      cracOnline: 3,          // One offline!
      coolingCapacity: 320,   // kW
      // Safety
      vesdaStatus: 'Normal',
      smokeLevel: 0.02,       // %/m obscuration
      fireSuppressionArmed: true,
    },
    racks: [
      { id: 'R-01', load: 22, inlet: 19, outlet: 34, status: 'normal' },
      { id: 'R-02', load: 24, inlet: 19, outlet: 36, status: 'normal' },
      { id: 'R-03', load: 21, inlet: 18, outlet: 33, status: 'normal' },
      { id: 'R-04', load: 23, inlet: 19, outlet: 35, status: 'normal' },
      { id: 'R-05', load: 20, inlet: 18, outlet: 32, status: 'normal' },
      { id: 'R-06', load: 22, inlet: 19, outlet: 34, status: 'normal' },
      { id: 'R-07', load: 24, inlet: 32, outlet: 48, status: 'critical' },  // HOT!
      { id: 'R-08', load: 21, inlet: 28, outlet: 42, status: 'warning' },   // Warm
      { id: 'R-09', load: 19, inlet: 20, outlet: 33, status: 'normal' },
      { id: 'R-10', load: 18, inlet: 19, outlet: 32, status: 'normal' },
      { id: 'R-11', load: 25, inlet: 19, outlet: 36, status: 'normal' },
      { id: 'R-12', load: 26, inlet: 20, outlet: 37, status: 'normal' },
    ],
    pdus: [
      { id: 'PDU-A1', feed: 'A', load: 62, capacity: 80, phases: [21, 20, 21] },
      { id: 'PDU-A2', feed: 'A', load: 62, capacity: 80, phases: [20, 21, 21] },
      { id: 'PDU-B1', feed: 'B', load: 60, capacity: 80, phases: [20, 20, 20] },
      { id: 'PDU-B2', feed: 'B', load: 61, capacity: 80, phases: [21, 19, 21] },
    ],
    cracs: [
      { id: 'CRAC-01', status: 'running', setpoint: 18, supply: 16, return: 34, load: 78 },
      { id: 'CRAC-02', status: 'fault', setpoint: 18, supply: null, return: null, load: 0, fault: 'Compressor short cycling - low refrigerant' },
      { id: 'CRAC-03', status: 'running', setpoint: 18, supply: 17, return: 35, load: 82 },
      { id: 'CRAC-04', status: 'running', setpoint: 18, supply: 16, return: 33, load: 75 },
    ],
  },
  {
    id: 'DATA-MOD-02',
    type: 'Compute Container',
    category: 'compute',
    model: 'Celerway MDC-40 High Density',
    dimensions: '40ft ISO',
    status: 'warning',
    location: 'Row A - Position 2',
    commissioned: '2024-09-15',
    rackCount: 12,
    maxRackPower: 25,
    currentLoad: 238,
    maxLoad: 300,
    metrics: {
      supplyAirTemp: 19,
      returnAirTemp: 36,
      deltaT: 17,
      humidity: 68,           // HIGH - condensation risk!
      dewPoint: 13,
      pressureDiff: 0.015,
      powerA: 120,
      powerB: 118,
      totalPower: 238,
      cracUnits: 4,
      cracOnline: 4,
      coolingCapacity: 320,
      vesdaStatus: 'Pre-Alarm',  // VESDA triggered!
      smokeLevel: 0.08,
      fireSuppressionArmed: true,
    },
    racks: [
      { id: 'R-13', load: 20, inlet: 19, outlet: 34, status: 'normal' },
      { id: 'R-14', load: 21, inlet: 19, outlet: 35, status: 'normal' },
      { id: 'R-15', load: 19, inlet: 18, outlet: 32, status: 'normal' },
      { id: 'R-16', load: 22, inlet: 19, outlet: 35, status: 'normal' },
      { id: 'R-17', load: 20, inlet: 19, outlet: 33, status: 'normal' },
      { id: 'R-18', load: 18, inlet: 18, outlet: 31, status: 'normal' },
      { id: 'R-19', load: 21, inlet: 19, outlet: 34, status: 'normal' },
      { id: 'R-20', load: 19, inlet: 18, outlet: 32, status: 'normal' },
      { id: 'R-21', load: 20, inlet: 19, outlet: 33, status: 'normal' },
      { id: 'R-22', load: 18, inlet: 18, outlet: 31, status: 'normal' },
      { id: 'R-23', load: 22, inlet: 19, outlet: 35, status: 'normal' },
      { id: 'R-24', load: 18, inlet: 18, outlet: 31, status: 'normal' },
    ],
    pdus: [
      { id: 'PDU-A3', feed: 'A', load: 60, capacity: 80, phases: [25, 18, 17] },  // Phase imbalance!
      { id: 'PDU-A4', feed: 'A', load: 60, capacity: 80, phases: [20, 20, 20] },
      { id: 'PDU-B3', feed: 'B', load: 59, capacity: 80, phases: [20, 19, 20] },
      { id: 'PDU-B4', feed: 'B', load: 59, capacity: 80, phases: [19, 20, 20] },
    ],
    cracs: [
      { id: 'CRAC-05', status: 'running', setpoint: 18, supply: 17, return: 35, load: 80 },
      { id: 'CRAC-06', status: 'running', setpoint: 18, supply: 17, return: 36, load: 78 },
      { id: 'CRAC-07', status: 'running', setpoint: 18, supply: 16, return: 34, load: 76 },
      { id: 'CRAC-08', status: 'running', setpoint: 18, supply: 17, return: 35, load: 79 },
    ],
  },
  {
    id: 'DATA-MOD-03',
    type: 'Compute Container',
    category: 'compute',
    model: 'Celerway MDC-40 High Density',
    dimensions: '40ft ISO',
    status: 'operational',
    location: 'Row A - Position 3',
    commissioned: '2024-10-01',
    rackCount: 12,
    maxRackPower: 25,
    currentLoad: 198,
    maxLoad: 300,
    metrics: {
      supplyAirTemp: 18,
      returnAirTemp: 33,
      deltaT: 15,
      humidity: 48,
      dewPoint: 7,
      pressureDiff: 0.02,
      powerA: 100,
      powerB: 98,
      totalPower: 198,
      cracUnits: 4,
      cracOnline: 4,
      coolingCapacity: 320,
      vesdaStatus: 'Normal',
      smokeLevel: 0.01,
      fireSuppressionArmed: true,
    },
    racks: [
      { id: 'R-25', load: 18, inlet: 18, outlet: 31, status: 'normal' },
      { id: 'R-26', load: 17, inlet: 18, outlet: 30, status: 'normal' },
      { id: 'R-27', load: 16, inlet: 17, outlet: 29, status: 'normal' },
      { id: 'R-28', load: 18, inlet: 18, outlet: 31, status: 'normal' },
      { id: 'R-29', load: 15, inlet: 17, outlet: 28, status: 'normal' },
      { id: 'R-30', load: 16, inlet: 18, outlet: 29, status: 'normal' },
      { id: 'R-31', load: 17, inlet: 18, outlet: 30, status: 'normal' },
      { id: 'R-32', load: 15, inlet: 17, outlet: 28, status: 'normal' },
      { id: 'R-33', load: 18, inlet: 18, outlet: 31, status: 'normal' },
      { id: 'R-34', load: 16, inlet: 18, outlet: 29, status: 'normal' },
      { id: 'R-35', load: 17, inlet: 18, outlet: 30, status: 'normal' },
      { id: 'R-36', load: 15, inlet: 17, outlet: 28, status: 'normal' },
    ],
    pdus: [
      { id: 'PDU-A5', feed: 'A', load: 50, capacity: 80, phases: [17, 16, 17] },
      { id: 'PDU-A6', feed: 'A', load: 50, capacity: 80, phases: [17, 17, 16] },
      { id: 'PDU-B5', feed: 'B', load: 49, capacity: 80, phases: [16, 17, 16] },
      { id: 'PDU-B6', feed: 'B', load: 49, capacity: 80, phases: [16, 16, 17] },
    ],
    cracs: [
      { id: 'CRAC-09', status: 'running', setpoint: 18, supply: 16, return: 32, load: 65 },
      { id: 'CRAC-10', status: 'running', setpoint: 18, supply: 17, return: 33, load: 68 },
      { id: 'CRAC-11', status: 'running', setpoint: 18, supply: 16, return: 32, load: 64 },
      { id: 'CRAC-12', status: 'standby', setpoint: 18, supply: null, return: null, load: 0 },
    ],
  },
  // ══════════════════════════════════════════════════════════════════════════
  // POWER CONTAINER
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'POWER-MOD-01',
    type: 'Power Container',
    category: 'power',
    model: 'Celerway MPC-40 Power Module',
    dimensions: '40ft ISO',
    status: 'operational',
    location: 'Row B - Position 1',
    commissioned: '2024-09-10',
    metrics: {
      // Utility Feed
      utilityStatus: 'Connected',
      utilityVoltage: 11000,       // V (11kV primary)
      utilityFrequency: 50,        // Hz
      utilityPF: 0.95,
      // Transformer
      transformerLoad: 68,         // %
      transformerTemp: 62,         // °C
      secondaryVoltage: 400,       // V
      // Generators (N+1)
      generators: [
        { id: 'GEN-01', status: 'standby', fuelLevel: 92, runHours: 245, lastTest: '2025-01-28' },
        { id: 'GEN-02', status: 'standby', fuelLevel: 22, runHours: 312, lastTest: '2025-01-28' },  // LOW FUEL!
      ],
      // ATS
      atsPosition: 'Utility',
      atsMode: 'Auto',
      lastTransfer: null,
      // Main Switchboard
      busVoltage: 400,
      busFrequency: 50,
      totalLoad: 720,             // kW
      peakLoad: 780,              // kW today
    },
  },
  // ══════════════════════════════════════════════════════════════════════════
  // COOLING CONTAINER
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'COOL-MOD-01',
    type: 'Cooling Plant Container',
    category: 'cooling',
    model: 'Celerway MCC-40 Cooling Module',
    dimensions: '40ft ISO',
    status: 'warning',
    location: 'Row B - Position 2',
    commissioned: '2024-09-10',
    metrics: {
      // Chillers
      chillers: [
        { id: 'CH-01', status: 'running', load: 75, supplyTemp: 7, returnTemp: 14, cop: 4.2 },
        { id: 'CH-02', status: 'running', load: 72, supplyTemp: 7, returnTemp: 13, cop: 3.6 },  // Low COP!
      ],
      // Pumps
      primaryPumps: { running: 2, standby: 1, flow: 85 },  // m³/h
      secondaryPumps: { running: 2, standby: 1, flow: 82 },
      // Free Cooling
      freeCoolingActive: true,
      freeCoolingPercent: 65,     // % of load via free cooling
      dryCoilerFanSpeed: 45,      // %
      ambientTemp: -5,            // °C
      // Glycol Loop
      glycolTemp: 5,              // °C
      glycolPressure: 2.4,        // bar
      glycolConcentration: 35,    // %
      // Overall
      totalCoolingCapacity: 1000, // kW
      currentCoolingLoad: 720,    // kW
      pue: 1.48,
      // Free cooling dampers
      damperPosition: 65,         // % (should be higher in this weather!)
      damperFault: true,          // Damper actuator issue
    },
  },
  // ══════════════════════════════════════════════════════════════════════════
  // UPS/BATTERY CONTAINER
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'UPS-MOD-01',
    type: 'UPS/Battery Container',
    category: 'ups',
    model: 'Celerway MUC-40 UPS Module',
    dimensions: '40ft ISO',
    status: 'warning',
    location: 'Row B - Position 3',
    commissioned: '2024-09-10',
    metrics: {
      // UPS Systems (2N)
      upsSystems: [
        { 
          id: 'UPS-A', 
          status: 'online', 
          mode: 'Double Conversion',
          inputVoltage: 400,
          outputVoltage: 400,
          load: 72,                // %
          efficiency: 96.2,
          runtime: 15,             // minutes at current load
        },
        { 
          id: 'UPS-B', 
          status: 'online', 
          mode: 'Double Conversion',
          inputVoltage: 400,
          outputVoltage: 400,
          load: 68,
          efficiency: 96.5,
          runtime: 15,
        },
      ],
      // Battery Strings (Li-ion)
      batteryStrings: [
        { id: 'BAT-A1', soc: 98, health: 97, temp: 24, voltage: 480, status: 'normal' },
        { id: 'BAT-A2', soc: 97, health: 96, temp: 25, voltage: 479, status: 'normal' },
        { id: 'BAT-B1', soc: 98, health: 98, temp: 24, voltage: 480, status: 'normal' },
        { id: 'BAT-B2', soc: 96, health: 94, temp: 26, voltage: 478, status: 'normal' },
        { id: 'BAT-B3', soc: 95, health: 82, temp: 31, voltage: 472, status: 'warning' },  // Degraded!
      ],
      // DC Bus
      dcBusVoltage: 480,
      dcBusStatus: 'Normal',
      // Totals
      totalRuntime: 15,           // minutes
      totalCapacity: 850,         // kWh
      remainingCapacity: 810,     // kWh
    },
  },
  // ══════════════════════════════════════════════════════════════════════════
  // NETWORK CONTAINER
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'NET-MOD-01',
    type: 'Network/MMR Container',
    category: 'network',
    model: 'Celerway MNC-20 Network Module',
    dimensions: '20ft ISO',
    status: 'operational',
    location: 'Row C - Position 1',
    commissioned: '2024-09-10',
    metrics: {
      // Core Switches
      coreSwitches: [
        { id: 'CORE-01', status: 'active', cpu: 35, ports: { total: 48, up: 42 }, throughput: 185 },
        { id: 'CORE-02', status: 'standby', cpu: 12, ports: { total: 48, up: 42 }, throughput: 0 },
      ],
      // Fiber
      fiberPanels: 8,
      fiberStrands: 288,
      strandsInUse: 156,
      // Carriers
      carriers: [
        { name: 'Telenor', status: 'up', bandwidth: 10, usage: 4.2 },   // Gbps
        { name: 'Telia', status: 'up', bandwidth: 10, usage: 3.8 },
      ],
      // Latency
      avgLatency: 2.4,            // ms to Oslo IX
      peakLatency: 8.2,
      // Security
      ddosMitigation: 'Active',
      firewallStatus: 'Online',
    },
  },
];

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  CRITICAL: { icon: '🔴', text: 'CRITICAL', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50', color: 'text-red-400' },
  WARNING: { icon: '🟡', text: 'WARNING', bg: 'bg-amber-500', glow: 'from-amber-500/50 to-yellow-500/50', color: 'text-amber-400' },
  OPTIMIZING: { icon: '⚡', text: 'OPTIMIZING', bg: 'bg-purple-500', glow: 'from-purple-500/50 to-violet-500/50', color: 'text-purple-400', pulse: true },
  NORMAL: { icon: '✓', text: 'NORMAL', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30', color: 'text-emerald-400' },
  STANDBY: { icon: '○', text: 'STANDBY', bg: 'bg-slate-500', glow: 'from-slate-500/30 to-gray-500/30', color: 'text-slate-400' },
};

const CATEGORY_STYLES = {
  compute: { icon: '🏗️', color: 'text-blue-300', bg: 'from-blue-400/30 to-cyan-500/30', border: 'border-blue-400/60', label: 'Compute' },
  power: { icon: '⚡', color: 'text-yellow-300', bg: 'from-yellow-400/30 to-amber-500/30', border: 'border-yellow-400/60', label: 'Power' },
  cooling: { icon: '❄️', color: 'text-cyan-300', bg: 'from-cyan-400/30 to-blue-500/30', border: 'border-cyan-400/60', label: 'Cooling' },
  ups: { icon: '🔋', color: 'text-emerald-300', bg: 'from-emerald-400/30 to-teal-500/30', border: 'border-emerald-400/60', label: 'UPS' },
  network: { icon: '🔌', color: 'text-purple-300', bg: 'from-purple-400/30 to-violet-500/30', border: 'border-purple-400/60', label: 'Network' },
};

// ════════════════════════════════════════════════════════════════════════════
// AI ANOMALY DETECTION - Modular DC Operations
// ════════════════════════════════════════════════════════════════════════════

const detectModularDCAnomalies = (containers, site) => {
  const anomalies = [];
  
  containers.forEach(container => {
    const m = container.metrics;
    
    // Compute Container Checks
    if (container.category === 'compute') {
      // Check for hot racks
      if (container.racks) {
        container.racks.forEach(rack => {
          if (rack.inlet > 27) {
            anomalies.push({
              id: `rack-hot-${rack.id}`,
              type: 'RACK_OVERTEMP',
              containerId: container.id,
              assetId: rack.id,
              severity: rack.inlet > 30 ? 'critical' : 'warning',
              metric: `${rack.inlet}°C inlet`,
              description: `Rack ${rack.id} inlet temperature ${rack.inlet}°C exceeds ASHRAE A1 limit. ${container.metrics.cracOnline < container.metrics.cracUnits ? 'CRAC unit offline in this container.' : 'Check airflow containment.'}`,
              recommendation: container.metrics.cracOnline < container.metrics.cracUnits 
                ? 'IMMEDIATE: Bring backup CRAC online. Check containment seals. Consider load migration.'
                : 'Verify hot/cold aisle containment. Check floor tiles and blanking panels. May need to reduce rack load.',
              confidence: 96,
              action: { type: 'ACTIVATE_COOLING', description: 'Start backup CRAC' },
            });
          }
        });
      }
      
      // CRAC fault
      if (container.cracs) {
        container.cracs.forEach(crac => {
          if (crac.status === 'fault') {
            anomalies.push({
              id: `crac-fault-${crac.id}`,
              type: 'CRAC_FAULT',
              containerId: container.id,
              assetId: crac.id,
              severity: 'critical',
              metric: crac.fault,
              description: `${crac.id} fault: ${crac.fault}. Cooling capacity reduced by 25%.`,
              recommendation: 'Dispatch technician immediately. Check refrigerant levels and compressor. May need to shed IT load if temps rise.',
              confidence: 98,
              action: { type: 'DISPATCH_TECH', description: 'Emergency HVAC call' },
            });
          }
        });
      }
      
      // PDU phase imbalance
      if (container.pdus) {
        container.pdus.forEach(pdu => {
          const maxPhase = Math.max(...pdu.phases);
          const minPhase = Math.min(...pdu.phases);
          const imbalance = ((maxPhase - minPhase) / maxPhase) * 100;
          
          if (imbalance > 15) {
            anomalies.push({
              id: `pdu-imbalance-${pdu.id}`,
              type: 'PDU_PHASE_IMBALANCE',
              containerId: container.id,
              assetId: pdu.id,
              severity: imbalance > 25 ? 'critical' : 'warning',
              metric: `${imbalance.toFixed(0)}% imbalance`,
              description: `${pdu.id} phase imbalance: ${pdu.phases.join('A / ')}A. Risk of neutral conductor overload.`,
              recommendation: 'Rebalance loads across phases. Review recent equipment additions. Check for failed PSUs causing single-phase operation.',
              confidence: 94,
              action: { type: 'REBALANCE_LOAD', description: 'Schedule load rebalancing' },
            });
          }
        });
      }
      
      // High humidity
      if (m.humidity > 60) {
        anomalies.push({
          id: `humidity-${container.id}`,
          type: 'HIGH_HUMIDITY',
          containerId: container.id,
          severity: m.humidity > 70 ? 'critical' : 'warning',
          metric: `${m.humidity}% RH`,
          description: `Humidity ${m.humidity}% exceeds recommended range. Condensation risk on cold surfaces.`,
          recommendation: 'Increase dehumidification. Check CRAC humidifier settings. Verify container seals.',
          confidence: 91,
          action: { type: 'ADJUST_HUMIDITY', description: 'Activate dehumidification' },
        });
      }
      
      // VESDA pre-alarm
      if (m.vesdaStatus === 'Pre-Alarm') {
        anomalies.push({
          id: `vesda-${container.id}`,
          type: 'VESDA_PREALARM',
          containerId: container.id,
          severity: 'critical',
          metric: `${m.smokeLevel}%/m`,
          description: `VESDA early warning in ${container.id}. Smoke level ${m.smokeLevel}%/m above baseline.`,
          recommendation: 'INVESTIGATE IMMEDIATELY. Check for overheating equipment, burnt components, or dust accumulation. Do not ignore.',
          confidence: 99,
          action: { type: 'INVESTIGATE_FIRE', description: 'Dispatch fire team' },
        });
      }
    }
    
    // Power Container Checks
    if (container.category === 'power') {
      // Generator low fuel
      if (m.generators) {
        m.generators.forEach(gen => {
          if (gen.fuelLevel < 30) {
            const hoursRemaining = (gen.fuelLevel / 100) * 72;  // Assume 72hr at full load
            anomalies.push({
              id: `gen-fuel-${gen.id}`,
              type: 'GENERATOR_LOW_FUEL',
              containerId: container.id,
              assetId: gen.id,
              severity: gen.fuelLevel < 20 ? 'critical' : 'warning',
              metric: `${gen.fuelLevel}% (${hoursRemaining.toFixed(0)}hr)`,
              description: `Generator ${gen.id} fuel level ${gen.fuelLevel}%. Approximately ${hoursRemaining.toFixed(0)} hours runtime remaining at rated load.`,
              recommendation: 'Schedule fuel delivery immediately. Verify backup generator availability.',
              confidence: 99,
              action: { type: 'ORDER_FUEL', description: 'Emergency fuel delivery' },
            });
          }
        });
      }
    }
    
    // Cooling Container Checks
    if (container.category === 'cooling') {
      // Chiller COP degradation
      if (m.chillers) {
        m.chillers.forEach(chiller => {
          if (chiller.cop < 4.0) {
            anomalies.push({
              id: `chiller-cop-${chiller.id}`,
              type: 'CHILLER_EFFICIENCY_LOW',
              containerId: container.id,
              assetId: chiller.id,
              severity: chiller.cop < 3.5 ? 'warning' : 'info',
              metric: `COP ${chiller.cop}`,
              description: `Chiller ${chiller.id} efficiency degraded. COP ${chiller.cop} vs expected 4.5+. Increased energy consumption.`,
              recommendation: 'Check condenser coils for fouling. Verify refrigerant charge. May need maintenance.',
              confidence: 87,
            });
          }
        });
      }
      
      // Free cooling damper fault
      if (m.damperFault) {
        anomalies.push({
          id: `damper-${container.id}`,
          type: 'FREE_COOLING_FAULT',
          containerId: container.id,
          severity: 'warning',
          metric: `Damper ${m.damperPosition}%`,
          description: `Free cooling dampers stuck at ${m.damperPosition}%. Ambient temp ${m.ambientTemp}°C ideal for free cooling but dampers not responding.`,
          recommendation: 'Check damper actuator. Manual override available. This fault is costing significant energy.',
          confidence: 92,
          action: { type: 'FIX_DAMPER', description: 'Dispatch controls tech' },
        });
      }
    }
    
    // UPS Container Checks
    if (container.category === 'ups') {
      // Battery health
      if (m.batteryStrings) {
        m.batteryStrings.forEach(battery => {
          if (battery.health < 85 || battery.temp > 30) {
            anomalies.push({
              id: `battery-${battery.id}`,
              type: battery.temp > 30 ? 'BATTERY_OVERTEMP' : 'BATTERY_DEGRADED',
              containerId: container.id,
              assetId: battery.id,
              severity: battery.health < 80 || battery.temp > 35 ? 'critical' : 'warning',
              metric: battery.temp > 30 ? `${battery.temp}°C` : `${battery.health}% health`,
              description: battery.temp > 30
                ? `Battery string ${battery.id} temperature ${battery.temp}°C exceeds limit. Thermal runaway risk.`
                : `Battery string ${battery.id} health degraded to ${battery.health}%. Reduced runtime capacity.`,
              recommendation: battery.temp > 30
                ? 'IMMEDIATE: Verify battery cooling. Check for cell failures. May need to isolate string.'
                : 'Schedule battery replacement during next maintenance window.',
              confidence: 95,
              action: battery.temp > 30 ? { type: 'ISOLATE_BATTERY', description: 'Isolate battery string' } : undefined,
            });
          }
        });
      }
    }
  });
  
  // Site-level PUE check
  if (site.pue > 1.5) {
    anomalies.push({
      id: 'pue-high',
      type: 'PUE_DEGRADED',
      containerId: 'SITE',
      severity: 'warning',
      metric: `PUE ${site.pue}`,
      description: `Site PUE ${site.pue} (was ${site.previousPUE}). Efficiency trending ${site.pueTrend}. Target: 1.3.`,
      recommendation: 'Review cooling optimization. Check free cooling utilization. Audit for energy waste.',
      confidence: 88,
      action: { type: 'OPTIMIZE_PUE', description: 'Run efficiency analysis' },
    });
  }
  
  return anomalies.sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getContainerAIStatus = (container, anomalies) => {
  const containerAnomalies = anomalies.filter(a => a.containerId === container.id);
  
  const hasCritical = containerAnomalies.some(a => a.severity === 'critical');
  const hasWarning = containerAnomalies.some(a => a.severity === 'warning');
  
  if (hasCritical || container.status === 'critical') return AI_STATUS.CRITICAL;
  if (hasWarning || container.status === 'warning') return AI_STATUS.WARNING;
  return AI_STATUS.NORMAL;
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// NOC Cloud Node
const NOCNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    
    <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-cyan-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-cyan-400/50 rounded-2xl p-6 min-w-[300px] shadow-2xl shadow-cyan-500/20">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-xl flex items-center justify-center border border-cyan-400/50">
          <span className="text-3xl">🌐</span>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-600/50 grid grid-cols-4 gap-2">
        <div className="text-center">
          <div className="text-lg font-bold text-cyan-300">{data.sites || 0}</div>
          <div className="text-[9px] text-slate-400 uppercase">Sites</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.containers || 0}</div>
          <div className="text-[9px] text-slate-400 uppercase">Modules</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.totalMW || 0}</div>
          <div className="text-[9px] text-slate-400 uppercase">MW</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-amber-300">{data.alerts || 0}</div>
          <div className="text-[9px] text-slate-400 uppercase">Alerts</div>
        </div>
      </div>
    </div>
  </div>
);

// Site Gateway Node
const SiteGatewayNode = ({ data }) => {
  const pueColor = data.pue > 1.5 ? 'text-amber-300' : data.pue > 1.3 ? 'text-cyan-300' : 'text-emerald-300';
  const loadPercent = Math.floor((data.totalITLoad / data.maxCapacity) * 100);
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      <div className="absolute -inset-1.5 bg-gradient-to-r from-emerald-400/30 to-cyan-400/30 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300" />
      
      <div className="relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 border-emerald-400/60 rounded-xl p-4 min-w-[360px] shadow-xl transition-all duration-300 group-hover:scale-105">
        <div className="absolute -top-1 -right-1 w-3 h-3">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400 shadow-lg" />
        </div>
        
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-emerald-500/30">
            <span className="text-2xl">🏭</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{data.location} • {data.siteType}</div>
          </div>
          <div className="text-right">
            <div className={`text-xl font-bold ${pueColor}`}>{data.pue}</div>
            <div className="text-[10px] text-slate-400">PUE {data.pueTrend === 'up' ? '📈' : '📉'}</div>
          </div>
        </div>
        
        {/* IT Load bar */}
        <div className="mt-3">
          <div className="flex justify-between text-[10px] mb-1">
            <span className="text-slate-400">IT Load</span>
            <span className="text-cyan-300 font-bold">{data.totalITLoad} / {data.maxCapacity} kW ({loadPercent}%)</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${loadPercent > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${loadPercent}%` }}
            />
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-slate-500/30 grid grid-cols-4 gap-2 text-xs">
          <div>
            <span className="text-slate-400">Containers</span>
            <div className="font-semibold text-cyan-300">{data.totalContainers}</div>
          </div>
          <div>
            <span className="text-slate-400">Racks</span>
            <div className="font-semibold text-blue-300">{data.totalRacks}</div>
          </div>
          <div>
            <span className="text-slate-400">Ambient</span>
            <div className="font-semibold text-cyan-300">{data.ambientTemp}°C</div>
          </div>
          <div>
            <span className="text-slate-400">Free Cool</span>
            <div className="font-semibold text-emerald-300">{data.freeCoolingActive ? 'ON' : 'OFF'}</div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
            {data.stratusModel} • {data.connectivity}
          </span>
        </div>
      </div>
    </div>
  );
};

// Container Module Node
const ContainerModuleNode = ({ data }) => {
  const categoryStyle = CATEGORY_STYLES[data.category] || CATEGORY_STYLES.compute;
  const aiStatus = data.aiStatus || AI_STATUS.NORMAL;
  const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
  const m = data.metrics || {};

  const renderMetrics = () => {
    switch (data.category) {
      case 'compute':
        const loadPercent = Math.floor((data.currentLoad / data.maxLoad) * 100);
        const cracIssue = m.cracOnline < m.cracUnits;
        return (
          <div className="space-y-2">
            {/* Load bar */}
            <div>
              <div className="flex justify-between text-[9px] mb-0.5">
                <span className="text-slate-400">{data.rackCount} Racks</span>
                <span className="text-cyan-300">{data.currentLoad}/{data.maxLoad}kW</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${loadPercent > 85 ? 'bg-amber-500' : 'bg-cyan-500'}`} style={{ width: `${loadPercent}%` }} />
              </div>
            </div>
            {/* Temps */}
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-cyan-300">{m.supplyAirTemp}°C</div>
                <div className="text-[8px] text-slate-500">COLD</div>
              </div>
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-orange-300">{m.returnAirTemp}°C</div>
                <div className="text-[8px] text-slate-500">HOT</div>
              </div>
              <div className={`rounded px-1 py-0.5 ${cracIssue ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
                <div className={`text-[10px] font-bold ${cracIssue ? 'text-red-300' : 'text-emerald-300'}`}>{m.cracOnline}/{m.cracUnits}</div>
                <div className="text-[8px] text-slate-500">CRAC</div>
              </div>
            </div>
            {/* VESDA status */}
            {m.vesdaStatus !== 'Normal' && (
              <div className="bg-red-500/20 border border-red-400/30 rounded px-2 py-1 text-center">
                <div className="text-[9px] font-bold text-red-300">🔥 VESDA {m.vesdaStatus}</div>
              </div>
            )}
          </div>
        );
      case 'power':
        return (
          <div className="space-y-1">
            <div className="grid grid-cols-2 gap-1 text-center">
              <div className="rounded px-1 py-0.5 bg-emerald-500/20">
                <div className="text-[10px] font-bold text-emerald-300">{m.atsPosition}</div>
                <div className="text-[8px] text-slate-500">SOURCE</div>
              </div>
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-yellow-300">{m.totalLoad}kW</div>
                <div className="text-[8px] text-slate-500">LOAD</div>
              </div>
            </div>
            {m.generators?.some(g => g.fuelLevel < 30) && (
              <div className="bg-amber-500/20 border border-amber-400/30 rounded px-2 py-1 text-center">
                <div className="text-[9px] font-bold text-amber-300">⛽ GEN FUEL LOW</div>
              </div>
            )}
          </div>
        );
      case 'cooling':
        return (
          <div className="space-y-1">
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-cyan-300">{m.chillers?.[0]?.supplyTemp}°C</div>
                <div className="text-[8px] text-slate-500">CHW</div>
              </div>
              <div className="rounded px-1 py-0.5 bg-emerald-500/20">
                <div className="text-[10px] font-bold text-emerald-300">{m.freeCoolingPercent}%</div>
                <div className="text-[8px] text-slate-500">FREE</div>
              </div>
              <div className={`rounded px-1 py-0.5 ${m.damperFault ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
                <div className={`text-[10px] font-bold ${m.damperFault ? 'text-amber-300' : 'text-slate-300'}`}>{m.damperPosition}%</div>
                <div className="text-[8px] text-slate-500">DAMP</div>
              </div>
            </div>
          </div>
        );
      case 'ups':
        const minHealth = Math.min(...(m.batteryStrings?.map(b => b.health) || [100]));
        return (
          <div className="space-y-1">
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="rounded px-1 py-0.5 bg-emerald-500/20">
                <div className="text-[10px] font-bold text-emerald-300">{m.upsSystems?.[0]?.mode?.split(' ')[0] || 'Online'}</div>
                <div className="text-[8px] text-slate-500">MODE</div>
              </div>
              <div className="rounded px-1 py-0.5 bg-slate-700/50">
                <div className="text-[10px] font-bold text-cyan-300">{m.totalRuntime}m</div>
                <div className="text-[8px] text-slate-500">RUNTIME</div>
              </div>
              <div className={`rounded px-1 py-0.5 ${minHealth < 85 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
                <div className={`text-[10px] font-bold ${minHealth < 85 ? 'text-amber-300' : 'text-slate-300'}`}>{minHealth}%</div>
                <div className="text-[8px] text-slate-500">HEALTH</div>
              </div>
            </div>
          </div>
        );
      case 'network':
        return (
          <div className="grid grid-cols-2 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-emerald-500/20">
              <div className="text-[10px] font-bold text-emerald-300">{m.carriers?.filter(c => c.status === 'up').length || 0}/{m.carriers?.length || 0}</div>
              <div className="text-[8px] text-slate-500">UPLINKS</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-cyan-300">{m.avgLatency}ms</div>
              <div className="text-[8px] text-slate-500">LATENCY</div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      {hasAlert && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? 'border-red-400' : 'border-amber-400') : categoryStyle.border} rounded-lg p-3 min-w-[180px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        
        {/* AI Status Badge */}
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        {/* Container header */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br ${categoryStyle.bg}`}>
            <span className="text-xl">{categoryStyle.icon}</span>
          </div>
          <div>
            <div className="text-[11px] font-bold text-white">{data.id}</div>
            <div className="text-[9px] text-slate-400">{data.type}</div>
          </div>
        </div>
        
        {/* Dimensions badge */}
        <div className="text-[9px] text-slate-500 mb-2">{data.dimensions} • {data.location}</div>
        
        {/* Metrics */}
        {renderMetrics()}
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  noc: NOCNode,
  siteGateway: SiteGatewayNode,
  containerModule: ContainerModuleNode,
};

// ════════════════════════════════════════════════════════════════════════════
// AI ACTIVITY PANEL
// ════════════════════════════════════════════════════════════════════════════

const AIActivityPanel = ({ isOpen, onClose, anomalies, containers, site }) => {
  const [activeTab, setActiveTab] = useState('alerts');
  const [executingAction, setExecutingAction] = useState(null);
  const [expandedContainer, setExpandedContainer] = useState(null);
  
  if (!isOpen) return null;
  
  const handleExecuteAction = async (anomaly) => {
    setExecutingAction(anomaly.id);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setExecutingAction(null);
  };
  
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const computeContainers = containers.filter(c => c.category === 'compute');
  
  return (
    <div className="absolute top-0 right-0 h-full w-[440px] bg-slate-900/98 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <span className="text-lg">🏭</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">AI Sentry Modular DC</h3>
              <div className="text-[10px] text-slate-400">{site.name} • {site.totalContainers} Modules</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 rounded text-emerald-300 text-xs">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            {site.availability}% Uptime
          </div>
          <div className={`text-[10px] px-2 py-1 rounded ${site.pue > 1.5 ? 'bg-amber-500/20 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
            PUE {site.pue}
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('alerts')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'alerts' ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            🚨 Alerts
            {criticalCount > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{criticalCount}</span>}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('containers')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'containers' ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white'
          }`}
        >
          🏗️ Containers
        </button>
        <button
          onClick={() => setActiveTab('efficiency')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'efficiency' ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10' : 'text-slate-400 hover:text-white'
          }`}
        >
          ⚡ Efficiency
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'alerts' && (
          <>
            {anomalies.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">✓</div>
                <div>All systems nominal</div>
              </div>
            ) : (
              anomalies.map(anomaly => {
                const container = containers.find(c => c.id === anomaly.containerId);
                const categoryStyle = CATEGORY_STYLES[container?.category] || CATEGORY_STYLES.compute;
                return (
                  <div
                    key={anomaly.id}
                    className={`bg-slate-800/50 rounded-lg border ${
                      anomaly.severity === 'critical' ? 'border-red-500/50' : 'border-amber-500/30'
                    } p-3`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{categoryStyle.icon}</span>
                        <div>
                          <div className="text-sm font-semibold text-white">{anomaly.containerId}</div>
                          <div className="text-[10px] text-slate-400">{anomaly.assetId || anomaly.type.replace(/_/g, ' ')}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-bold ${anomaly.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                          {anomaly.confidence}%
                        </div>
                        <div className="text-[10px] text-slate-500">confidence</div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">{anomaly.description}</div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${anomaly.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {anomaly.metric}
                      </span>
                    </div>
                    
                    <div className="bg-slate-700/30 rounded p-2 mb-3">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">AI Recommendation</div>
                      <div className="text-xs text-cyan-300">{anomaly.recommendation}</div>
                    </div>
                    
                    {anomaly.action && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleExecuteAction(anomaly)}
                          disabled={executingAction === anomaly.id}
                          className={`flex-1 px-3 py-1.5 ${anomaly.severity === 'critical' ? 'bg-red-500 hover:bg-red-400' : 'bg-cyan-500 hover:bg-cyan-400'} disabled:bg-slate-600 text-white text-xs font-medium rounded transition-colors flex items-center justify-center gap-1`}
                        >
                          {executingAction === anomaly.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Executing...
                            </>
                          ) : (
                            <>✓ {anomaly.action.type.replace(/_/g, ' ')}</>
                          )}
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors">
                          Acknowledge
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
        
        {activeTab === 'containers' && (
          <div className="space-y-3">
            {computeContainers.map(container => {
              const isExpanded = expandedContainer === container.id;
              return (
                <div key={container.id} className="bg-slate-800/50 rounded-lg border border-slate-600/30">
                  <button
                    onClick={() => setExpandedContainer(isExpanded ? null : container.id)}
                    className="w-full p-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🏗️</span>
                      <div className="text-left">
                        <div className="text-sm font-semibold text-white">{container.id}</div>
                        <div className="text-[10px] text-slate-400">{container.rackCount} Racks • {container.currentLoad}kW</div>
                      </div>
                    </div>
                    <svg className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      <div className="text-[10px] text-slate-400 uppercase">Rack Temperatures</div>
                      <div className="grid grid-cols-4 gap-1">
                        {container.racks.map(rack => (
                          <div
                            key={rack.id}
                            className={`p-1.5 rounded text-center ${
                              rack.inlet > 30 ? 'bg-red-500/20 border border-red-400/30' :
                              rack.inlet > 27 ? 'bg-amber-500/20 border border-amber-400/30' :
                              'bg-slate-700/50'
                            }`}
                          >
                            <div className="text-[10px] font-bold text-white">{rack.id}</div>
                            <div className={`text-[9px] ${rack.inlet > 30 ? 'text-red-300' : rack.inlet > 27 ? 'text-amber-300' : 'text-slate-300'}`}>
                              {rack.inlet}°C
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="text-[10px] text-slate-400 uppercase mt-2">CRAC Status</div>
                      <div className="grid grid-cols-2 gap-1">
                        {container.cracs.map(crac => (
                          <div
                            key={crac.id}
                            className={`p-1.5 rounded ${
                              crac.status === 'fault' ? 'bg-red-500/20 border border-red-400/30' :
                              crac.status === 'standby' ? 'bg-slate-700/50' :
                              'bg-emerald-500/10 border border-emerald-400/30'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-white">{crac.id}</span>
                              <span className={`text-[9px] ${
                                crac.status === 'fault' ? 'text-red-300' :
                                crac.status === 'standby' ? 'text-slate-400' :
                                'text-emerald-300'
                              }`}>
                                {crac.status === 'fault' ? '⚠️ FAULT' : crac.status === 'standby' ? 'STANDBY' : `${crac.load}%`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {activeTab === 'efficiency' && (
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Power Usage Effectiveness</div>
              <div className="flex items-center gap-4">
                <div className={`w-20 h-20 rounded-full border-4 ${site.pue > 1.5 ? 'border-amber-500' : 'border-emerald-500'} flex items-center justify-center`}>
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${site.pue > 1.5 ? 'text-amber-300' : 'text-emerald-300'}`}>{site.pue}</div>
                    <div className="text-[10px] text-slate-400">PUE</div>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-sm text-white">Target: 1.30</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {site.pue > 1.5 ? '⚠️ Above target - optimization needed' : '✓ Within acceptable range'}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-2">
                    Previous: {site.previousPUE} • Trend: {site.pueTrend === 'up' ? '📈 Rising' : '📉 Falling'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Energy Breakdown</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-300">IT Load</span>
                  <span className="text-xs font-bold text-cyan-300">{site.totalITLoad} kW</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-300">Cooling</span>
                  <span className="text-xs font-bold text-blue-300">{Math.floor(site.totalITLoad * (site.pue - 1) * 0.7)} kW</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-300">Other (UPS, lights, etc)</span>
                  <span className="text-xs font-bold text-slate-300">{Math.floor(site.totalITLoad * (site.pue - 1) * 0.3)} kW</span>
                </div>
                <div className="border-t border-slate-600/50 pt-2 flex items-center justify-between">
                  <span className="text-xs text-white font-semibold">Total Facility Power</span>
                  <span className="text-xs font-bold text-amber-300">{Math.floor(site.totalITLoad * site.pue)} kW</span>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Free Cooling</div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <span className="text-xl">❄️</span>
                </div>
                <div>
                  <div className="text-lg font-bold text-cyan-300">
                    {containers.find(c => c.category === 'cooling')?.metrics.freeCoolingPercent || 0}%
                  </div>
                  <div className="text-xs text-slate-400">Free cooling utilization</div>
                  <div className="text-[10px] text-slate-500">Ambient: {site.ambientTemp}°C</div>
                </div>
              </div>
              {containers.find(c => c.category === 'cooling')?.metrics.damperFault && (
                <div className="mt-2 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                  <div className="text-[10px] text-amber-300">⚠️ Free cooling damper fault - not operating at optimal efficiency</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-800/50">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>DCIM sync: 2s ago</span>
          <button className="text-cyan-400 hover:text-cyan-300">Refresh Now</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT & STATS
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlert = ({ anomalies, containers, onViewDetails }) => {
  const criticalAnomalies = anomalies.filter(a => a.severity === 'critical');
  if (criticalAnomalies.length === 0) return null;
  
  // Prioritize fire/VESDA
  const fireAnomaly = criticalAnomalies.find(a => a.type === 'VESDA_PREALARM');
  const topAnomaly = fireAnomaly || criticalAnomalies[0];
  const container = containers.find(c => c.id === topAnomaly.containerId);
  const categoryStyle = CATEGORY_STYLES[container?.category] || CATEGORY_STYLES.compute;
  
  return (
    <div className="absolute top-32 left-6 z-40 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/95 to-slate-900/95 backdrop-blur-xl border border-red-500/50 rounded-xl p-4 shadow-2xl shadow-red-500/20 max-w-md">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-2xl animate-pulse">{categoryStyle.icon}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white mb-1">
              {topAnomaly.type === 'VESDA_PREALARM' ? '🔥 VESDA PRE-ALARM' : 
               topAnomaly.type === 'RACK_OVERTEMP' ? '🌡️ RACK OVERTEMP' :
               'DC ALERT'}
            </div>
            <div className="text-xs text-red-200 mb-2">{topAnomaly.description}</div>
            <div className="text-[10px] text-slate-400 mb-3">
              {topAnomaly.containerId} • {topAnomaly.assetId || ''} • {criticalAnomalies.length > 1 ? `+${criticalAnomalies.length - 1} more` : ''}
            </div>
            <button 
              onClick={onViewDetails}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatsBar = ({ site, anomalies, onOpenPanel }) => {
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const loadPercent = Math.floor((site.totalITLoad / site.maxCapacity) * 100);
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-cyan-300 font-bold text-lg">{site.totalITLoad}</span>
        <span className="text-slate-300 text-xs">kW</span>
      </div>
      <div className={`flex items-center gap-2 rounded-lg px-3 py-2 border ${site.pue > 1.5 ? 'bg-amber-500/20 border-amber-400/50' : 'bg-slate-700/50 border-slate-600/30'}`}>
        <span className={`font-bold text-lg ${site.pue > 1.5 ? 'text-amber-300' : 'text-emerald-300'}`}>{site.pue}</span>
        <span className="text-slate-300 text-xs">PUE</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-blue-300 font-bold text-lg">{loadPercent}%</span>
        <span className="text-slate-300 text-xs">Load</span>
      </div>
      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
          <span className="text-red-300 font-bold text-lg">{criticalCount}</span>
          <span className="text-red-300/70 text-xs">CRITICAL</span>
        </div>
      )}
      <button
        onClick={onOpenPanel}
        className="flex items-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg px-4 py-2 border border-cyan-400/50 transition-colors"
      >
        <span className="text-cyan-300 text-sm">🤖</span>
        <span className="text-cyan-300 text-xs font-medium">AI Sentry</span>
        {anomalies.length > 0 && (
          <span className="px-1.5 py-0.5 bg-cyan-500 text-white text-[10px] rounded-full">{anomalies.length}</span>
        )}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Container Modules</div>
    <div className="space-y-2">
      {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-sm">{style.icon}</span>
          <span className="text-xs text-slate-300">{style.label}</span>
        </div>
      ))}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const ModularDCSentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [containers] = useState(MOCK_CONTAINERS);
  const [site] = useState(MOCK_SITE_GATEWAY);
  const [anomalies, setAnomalies] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const buildTopology = useCallback(() => {
    const detectedAnomalies = detectModularDCAnomalies(containers, site);
    setAnomalies(detectedAnomalies);
    
    const newNodes = [];
    const newEdges = [];
    
    const CLOUD_Y = 0;
    const SITE_Y = 200;
    const CONTAINER_Y_START = 450;
    const CENTER_X = 550;
    const CONTAINER_GAP = 210;
    const ROW_GAP = 180;
    
    // Group by category
    const computeContainers = containers.filter(c => c.category === 'compute');
    const infraContainers = containers.filter(c => c.category !== 'compute');
    
    // NOC
    newNodes.push({
      id: 'noc',
      type: 'noc',
      position: { x: CENTER_X, y: CLOUD_Y },
      data: {
        label: 'Modular DC Operations',
        sublabel: 'Cloud NOC - 24/7 Monitoring',
        sites: 3,
        containers: site.totalContainers,
        totalMW: (site.totalITLoad / 1000).toFixed(1),
        alerts: detectedAnomalies.length,
      },
    });
    
    // Site Gateway
    newNodes.push({
      id: `site-${site.id}`,
      type: 'siteGateway',
      position: { x: CENTER_X, y: SITE_Y },
      data: site,
    });
    
    newEdges.push({
      id: 'noc-to-site',
      source: 'noc',
      target: `site-${site.id}`,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#22d3ee', strokeWidth: 2 },
    });
    
    // Compute Containers (Row 1)
    const computeStartX = CENTER_X - ((computeContainers.length - 1) * CONTAINER_GAP) / 2;
    computeContainers.forEach((container, idx) => {
      const aiStatus = getContainerAIStatus(container, detectedAnomalies);
      const containerId = `container-${container.id}`;
      
      newNodes.push({
        id: containerId,
        type: 'containerModule',
        position: { x: computeStartX + idx * CONTAINER_GAP, y: CONTAINER_Y_START },
        data: { ...container, aiStatus },
      });
      
      const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
      const edgeColor = hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? '#ef4444' : '#f59e0b') : '#22d3ee';
      
      newEdges.push({
        id: `site-to-${containerId}`,
        source: `site-${site.id}`,
        target: containerId,
        type: 'smoothstep',
        animated: true,
        style: { stroke: edgeColor, strokeWidth: 2 },
      });
    });
    
    // Infrastructure Containers (Row 2)
    const infraStartX = CENTER_X - ((infraContainers.length - 1) * CONTAINER_GAP) / 2;
    infraContainers.forEach((container, idx) => {
      const aiStatus = getContainerAIStatus(container, detectedAnomalies);
      const containerId = `container-${container.id}`;
      
      newNodes.push({
        id: containerId,
        type: 'containerModule',
        position: { x: infraStartX + idx * CONTAINER_GAP, y: CONTAINER_Y_START + ROW_GAP },
        data: { ...container, aiStatus },
      });
      
      const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
      const edgeColor = hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? '#ef4444' : '#f59e0b') : '#22d3ee';
      
      newEdges.push({
        id: `site-to-${containerId}`,
        source: `site-${site.id}`,
        target: containerId,
        type: 'smoothstep',
        animated: true,
        style: { stroke: edgeColor, strokeWidth: 2 },
      });
    });
    
    return { nodes: newNodes, edges: newEdges };
  }, [containers, site]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 1000);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-cyan-500/30 to-blue-600/30 rounded-xl flex items-center justify-center border border-cyan-400/30">
                <span className="text-xl">🏭</span>
              </span>
              Modular DC Sentry
            </h1>
            <p className="text-slate-300 text-sm mt-1">AI-Powered Containerized Data Center • High-Density Edge Computing</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded border border-cyan-500/30">
                {site.name} • {site.siteType}
              </span>
              <span className="text-[10px] px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded border border-emerald-500/30">
                Celerway MDC Platform
              </span>
              <span className="text-[10px] px-2 py-1 bg-slate-700/50 text-slate-400 rounded">
                MWC Barcelona 2025
              </span>
            </div>
          </div>
          <StatsBar site={site} anomalies={anomalies} onOpenPanel={() => setIsAIPanelOpen(true)} />
        </div>
      </div>
      
      <ProactiveAlert anomalies={anomalies} containers={containers} onViewDetails={() => setIsAIPanelOpen(true)} />
      
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-300 text-lg font-medium">AI Sentry Initializing...</div>
            <div className="text-slate-400 text-sm mt-1">Connecting to DCIM</div>
          </div>
        </div>
      )}
      
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(34, 211, 238, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'noc') return '#22d3ee';
              if (node.type === 'siteGateway') return '#34d399';
              if (node.data?.aiStatus === AI_STATUS.CRITICAL) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.WARNING) return '#f59e0b';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      <AIActivityPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        anomalies={anomalies}
        containers={containers}
        site={site}
      />
      
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default ModularDCSentry;
